import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import CustomTooltip from "./CustomTooltip";
import { RefObject, useState } from "react";
import "./styles/Pie.scss"
import { IAnalyticsDataItem } from "../types";
import { useTranslation } from "react-i18next";
import { formatNumber } from "./formatValue";

interface ICustomPie {
	data: IAnalyticsDataItem[] | null
	total: "time" | "cost"
	pieRef: RefObject<HTMLDivElement>
}

const darkenColor = (hex: string, percent: number): string => {
	hex = hex.replace(/^#/, "");

	let r = parseInt(hex.substring(0, 2), 16);
	let g = parseInt(hex.substring(2, 4), 16);
	let b = parseInt(hex.substring(4, 6), 16);

	r = Math.max(0, Math.floor(r * (1 - percent / 100)));
	g = Math.max(0, Math.floor(g * (1 - percent / 100)));
	b = Math.max(0, Math.floor(b * (1 - percent / 100)));

	const toHex = (c: number) => c.toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export default function CustomPie({ data, total, pieRef }: ICustomPie) {

	const 
	[hoverIndex, setHoverIndex] = useState<number | null>(null),
	[tooltipPos, setTooltipPos] = useState<{ x: number, y: number }>({ x: 0, y: 0 }),
	{ t } = useTranslation();

	let totalValue = 0, hoverColors: (string)[] = [];
	// eslint-disable-next-line array-callback-return
	if(data) data.map(entry => {
		hoverColors.push(darkenColor(entry.color, 20));
		const value = entry.value;
		totalValue += value;
	})

	const value = `${formatNumber(totalValue, total === "time" ? 1 : 2).toLocaleString()}${total === "time" ? ` ${t("analytics_page.pie.h")}` : ` ${t("analytics_page.pie.uah")}`}`;

	return <ResponsiveContainer width="100%" height="100%">
		<div className="pie-chart" ref={pieRef}>
			<div className={`pie-chart__total${value.length > 11 ? " is-min" : ""}`}>
				<span>{total === "time" ? t("analytics_page.pie.total_time") : t("analytics_page.pie.total_cost")}:</span><br />
				<b>{value}</b>
			</div>
			<PieChart width={220} height={220}>
				<Pie
					dataKey="percentage"
					isAnimationActive={false}
					data={data && data.length ? data : [{ name: "", value: "", color: "#808080", percentage: 100 }]}
					cx="50%"
					cy="50%"
					startAngle={90}
        			endAngle={-270}
					outerRadius={105}
					innerRadius={75}
					fill="#8884d8"
				>
					{data ? data.map((entry, index) => (
						<Cell key={`${entry.name}-cell-${index}`} fill={entry.color}
							stroke={hoverIndex === index ? hoverColors[index] : "transparent"}
							strokeWidth={hoverIndex === index ? 2 : 0}
							onMouseEnter={(event) => {
								setHoverIndex(index);

								const { current } = pieRef;
								if(current) {
									if(window.innerWidth >= 992) {
										const { left, top } = current.getBoundingClientRect();
										setTooltipPos({ x: event.clientX + 10 - left, y: event.clientY - 40 - top });
									} else {
										const { left } = current.getBoundingClientRect();
										setTooltipPos({ x: -left + 15, y: -25 });
									}
								}
							}}
							onMouseMove={(event) => {
								const { current } = pieRef;
								if(current) {
									if(window.innerWidth >= 992) {
										const { left, top } = current.getBoundingClientRect();
										setTooltipPos({ x: event.clientX + 10 - left, y: event.clientY - 40 - top });
									} else {
										const { left } = current.getBoundingClientRect();
										setTooltipPos({ x: -left + 15, y: -25 });
									}
								}
							}}
							onMouseLeave={() => setHoverIndex(null)}
							style={{ transition: "fill 0.3s, stroke 0.3s, stroke-width 0.3s" }}
						/>
					)) : <Cell key={`none-cell`} fill="#BFC5D5" stroke={"transparent"} strokeWidth={0} />}
				</Pie>
				{data && data.length !== 0 && <Tooltip position={tooltipPos} content={<CustomTooltip type={total} />} />}
			</PieChart>
		</div>
	</ResponsiveContainer>
}